import { Fragment, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui/card'
import { formatDateTime } from '@/lib/dates'
import { formatPhone } from '@/lib/format'

/**
 * Eric activity table — the read-only flat list of inbound member-to-
 * Eric requests with the expandable tool-call trail. Rendered in two
 * places:
 *
 *   1. /admin/eric-activity — cross-tenant feed (no tenant filter)
 *   2. /admin/tenants/:id    — Eric activity tab, scoped to one tenant
 *
 * The data shape, expand UX, and decision pill / channel chip styling
 * are identical across both surfaces; the only difference is the API
 * filter query and whether the "Tenant" column appears (we hide it
 * when the parent is already a single-tenant view).
 *
 * Refreshes every 10 seconds — useful during real-world testing at the
 * shop. Auto-refresh is cheap on the worker (an indexed table scan
 * with a small LIMIT) and the row volume is well under the 100-row
 * default cap today.
 */

type ToolCall = {
  name: string
  input: Record<string, unknown>
  output: unknown
  ms?: number | null
  error?: string | null
}

export type EricActivityEntry = {
  id: string
  shop_id: string
  shop_name: string | null
  clerk_user_id: string | null
  channel: string
  // How the user supplied the turn: 'text' (typed, the default), 'photo' (a
  // staged photo whose extraction shipped as context), or 'voice'. Surfaced as
  // a small badge so photo usage is visible (every row read 'text' before the
  // dispatcher started writing this).
  input_modality: string
  channel_user_id: string
  channel_message_id: string
  request_text: string
  guardrail_decision: string
  tool_calls: ToolCall[] | null
  final_reply: string | null
  error: string | null
  ms_total: number | null
  // v3-brain reasoning trace — null for v2 rows + pre-brain refusals.
  // Shape is owned by worker/lib/eric-brain-v3.ts ReasoningTrace; we
  // type it loose here so the worker can extend without a frontend
  // PR. The detail row pretty-prints the whole payload.
  reasoning: Record<string, unknown> | null
  created_at: string
  // Actor name/email from users join. Populated when clerk_user_id is
  // set AND the user has a row in autoflo's users table. Falls back
  // through clerk_user_id → channel_user_id (phone) in the render.
  actor_name: string | null
  actor_email: string | null
}

type Resp = { entries: EricActivityEntry[] }

export type EricActivityRange = '24h' | '7d' | '30d' | 'all'
export type EricActivityChannel =
  | 'all'
  | 'sms'
  | 'in_app'
  | 'voice_note'
  | 'dashboard'

export type EricActivityTableProps = {
  // Pass to scope server-side to a single tenant; the worker enforces
  // shop_id from the URL param so this is for display + the API call,
  // not authorization.
  shopId?: string
  // Default range. Spec: tenant tab opens at 30d; cross-tenant page
  // stays at 'all' for parity with prior behavior.
  defaultRange?: EricActivityRange
  // Hide the per-row tenant chip (the line under "From" that shows
  // shop name). The cross-tenant page wants this on; the tenant tab
  // wants it off — the parent already names the tenant in the page
  // header.
  hideShopColumn?: boolean
  // Show the inline Range + Channel filter row above the table.
  // Tenant tab passes true (per spec); cross-tenant page passes false
  // so its behavior doesn't change in this drop.
  showFilters?: boolean
  // Refetch cadence in ms. Default 10s for real-time feel; set to 0 to
  // disable polling.
  refetchIntervalMs?: number
  // Stable key prefix so the cross-tenant + per-tenant feeds don't
  // collide in TanStack Query's cache. Defaults to 'admin.eric-activity'.
  queryKeyPrefix?: string
}

export function EricActivityTable({
  shopId,
  defaultRange = 'all',
  hideShopColumn = false,
  showFilters = false,
  refetchIntervalMs = 10_000,
  queryKeyPrefix = 'admin.eric-activity',
}: EricActivityTableProps) {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const [expanded, setExpanded] = useState<string | null>(null)
  const [detailTab, setDetailTab] = useState<'tool_calls' | 'reasoning'>(
    'tool_calls',
  )
  const [range, setRange] = useState<EricActivityRange>(defaultRange)
  const [channel, setChannel] = useState<EricActivityChannel>('all')

  const query = useQuery<Resp>({
    queryKey: [queryKeyPrefix, { shopId: shopId ?? null, range, channel }],
    enabled: isLoaded,
    refetchInterval: refetchIntervalMs > 0 ? refetchIntervalMs : false,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const params = new URLSearchParams({ limit: '100' })
      if (shopId) params.set('shop_id', shopId)
      if (range !== 'all') params.set('range', range)
      if (channel !== 'all') params.set('channel', channel)
      const res = await fetch(`/api/admin/eric-activity?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        throw new Error(
          tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Resp
    },
  })

  const entries = query.data?.entries ?? []

  return (
    <div className="space-y-3">
      {showFilters ? (
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <label className="flex items-center gap-2">
            <span className="font-semibold uppercase tracking-wide text-muted-foreground">
              {t('eric_activity.filter.range')}
            </span>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value as EricActivityRange)}
              className="rounded-md border border-border bg-background px-2 py-1"
            >
              <option value="24h">{t('eric_activity.range.h24')}</option>
              <option value="7d">{t('eric_activity.range.d7')}</option>
              <option value="30d">{t('eric_activity.range.d30')}</option>
              <option value="all">{t('eric_activity.range.all')}</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="font-semibold uppercase tracking-wide text-muted-foreground">
              {t('eric_activity.filter.channel')}
            </span>
            <select
              value={channel}
              onChange={(e) =>
                setChannel(e.target.value as EricActivityChannel)
              }
              className="rounded-md border border-border bg-background px-2 py-1"
            >
              <option value="all">{t('eric_activity.channel.all')}</option>
              <option value="sms">{t('eric_activity.channel.sms')}</option>
              <option value="in_app">
                {t('eric_activity.channel.in_app')}
              </option>
              <option value="dashboard">
                {t('eric_activity.channel.dashboard')}
              </option>
              <option value="voice_note">
                {t('eric_activity.channel.voice_note')}
              </option>
            </select>
          </label>
          {query.data ? (
            <span className="text-muted-foreground">
              {t('eric_activity.filter.summary', {
                count: entries.length,
              })}
            </span>
          ) : null}
        </div>
      ) : null}

      {query.isError ? (
        <Card className="border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof Error
            ? query.error.message
            : t('eric_activity.error')}
        </Card>
      ) : null}
      {query.isLoading ? (
        <Card className="p-4 text-sm text-muted-foreground">
          {tCommon('loading')}
        </Card>
      ) : null}

      {query.data && entries.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {t('eric_activity.empty')}
        </Card>
      ) : null}

      {query.data && entries.length > 0 ? (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">
                    {t('eric_activity.table.when')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('eric_activity.table.from')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('eric_activity.table.request')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('eric_activity.table.decision')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('eric_activity.table.tools')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('eric_activity.table.duration')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const isOpen = expanded === e.id
                  return (
                    <Fragment key={e.id}>
                      <tr
                        className="cursor-pointer border-b border-border/60 hover:bg-muted/30 last:border-0"
                        onClick={() => setExpanded(isOpen ? null : e.id)}
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                          {formatDateTime(e.created_at)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-foreground">
                          <FromCell entry={e} hideShop={hideShopColumn} />
                        </td>
                        <td className="max-w-[26rem] truncate px-4 py-3 text-foreground">
                          {e.input_modality === 'photo' ||
                          e.input_modality === 'voice' ? (
                            <span className="mr-1.5 rounded bg-muted px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                              {t(`eric_activity.modality.${e.input_modality}`)}
                            </span>
                          ) : null}
                          {e.request_text}
                        </td>
                        <td className="px-4 py-3">
                          <DecisionPill decision={e.guardrail_decision} />
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {e.tool_calls?.length
                            ? t('eric_activity.tools_count', {
                                count: e.tool_calls.length,
                              })
                            : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-muted-foreground">
                          {e.ms_total === null ? '—' : `${e.ms_total} ms`}
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="border-b border-border/60 bg-muted/20">
                          <td colSpan={6} className="px-4 py-4">
                            <div className="space-y-3 text-xs">
                              <DetailRow
                                label={t('eric_activity.detail.request')}
                                value={e.request_text}
                              />
                              <DetailRow
                                label={t('eric_activity.detail.reply')}
                                value={
                                  e.final_reply ?? `(${tCommon('no_value')})`
                                }
                              />
                              {e.error ? (
                                <DetailRow
                                  label={t('eric_activity.detail.error')}
                                  value={e.error}
                                  className="text-destructive"
                                />
                              ) : null}
                              {(e.tool_calls?.length ?? 0) > 0 || e.reasoning ? (
                                <div>
                                  <div className="flex gap-1 border-b border-border/60">
                                    <TabButton
                                      label={t('eric_activity.detail.tab.tool_calls')}
                                      active={detailTab === 'tool_calls'}
                                      onClick={() => setDetailTab('tool_calls')}
                                    />
                                    <TabButton
                                      label={t('eric_activity.detail.tab.reasoning')}
                                      active={detailTab === 'reasoning'}
                                      onClick={() => setDetailTab('reasoning')}
                                    />
                                  </div>
                                  {detailTab === 'tool_calls' ? (
                                    e.tool_calls?.length ? (
                                      <pre className="mt-2 max-h-96 overflow-auto rounded border border-border bg-background p-3 font-mono text-[11px] text-foreground">
                                        {JSON.stringify(e.tool_calls, null, 2)}
                                      </pre>
                                    ) : (
                                      <div className="mt-2 text-muted-foreground">
                                        ({tCommon('no_value')})
                                      </div>
                                    )
                                  ) : null}
                                  {detailTab === 'reasoning' ? (
                                    e.reasoning ? (
                                      <pre className="mt-2 max-h-96 overflow-auto rounded border border-border bg-background p-3 font-mono text-[11px] text-foreground">
                                        {JSON.stringify(e.reasoning, null, 2)}
                                      </pre>
                                    ) : (
                                      <div className="mt-2 text-muted-foreground">
                                        {t('eric_activity.detail.reasoning_empty')}
                                      </div>
                                    )
                                  ) : null}
                                </div>
                              ) : null}
                              <div className="font-mono text-[10px] text-muted-foreground">
                                msg id: {e.channel_message_id} · user:{' '}
                                {e.clerk_user_id ?? '(unauthorized)'}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  )
}

// From-cell renderer. When the row has a clerk_user_id, attribute to
// the firing user (name → email → raw clerk id) with the channel
// identifier as subtitle. When the user is unknown (anon SMS), show
// the phone/channel id as the primary line — same as the prior
// behavior. Either way the channel chip + shop name stays in the
// subtitle row so the metadata is preserved.
function FromCell({
  entry: e,
  hideShop,
}: {
  entry: EricActivityEntry
  hideShop: boolean
}) {
  const hasUser = Boolean(e.clerk_user_id)
  const userLabel = hasUser
    ? (e.actor_name ?? e.actor_email ?? e.clerk_user_id ?? '—')
    : null
  // Channel subtitle: SMS / WhatsApp get a formatted phone number
  // (channel_user_id is E.164). For in_app / dashboard / voice_note
  // the channel_user_id is just the clerk_user_id repeated — hide it
  // to avoid a cryptic "user_29Xx..." subtitle under the actor name.
  // The channel chip + shop name on the row below already convey the
  // origin without it.
  const channelLabel =
    e.channel === 'sms' || e.channel === 'whatsapp'
      ? formatPhone(e.channel_user_id)
      : e.channel === 'in_app' ||
          e.channel === 'dashboard' ||
          e.channel === 'voice_note'
        ? null
        : e.channel_user_id
  return (
    <>
      {userLabel ? (
        <>
          <div className="text-xs font-medium text-foreground">
            👤 {userLabel}
          </div>
          {channelLabel ? (
            <div className="font-mono text-[11px] text-muted-foreground">
              {channelLabel}
            </div>
          ) : null}
        </>
      ) : (
        <div className="font-mono text-xs">{channelLabel ?? '—'}</div>
      )}
      <div className="text-[11px] text-muted-foreground">
        <ChannelChip channel={e.channel} />
        {hideShop ? null : ` · ${e.shop_name ?? e.shop_id}`}
      </div>
    </>
  )
}

function DecisionPill({ decision }: { decision: string }) {
  const { t } = useTranslation('admin')
  const tone: 'ok' | 'warn' | 'bad' =
    decision === 'allow'
      ? 'ok'
      : decision.startsWith('refuse_')
        ? 'warn'
        : 'bad'
  const cls = {
    ok: 'bg-emerald-100 text-emerald-800',
    warn: 'bg-amber-100 text-amber-800',
    bad: 'bg-rose-100 text-rose-800',
  }[tone]
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {t(`eric_activity.decision.${decision}`, decision)}
    </span>
  )
}

function ChannelChip({ channel }: { channel: string }) {
  const { t } = useTranslation('admin')
  const known =
    channel === 'sms' ||
    channel === 'in_app' ||
    channel === 'voice_note' ||
    channel === 'whatsapp' ||
    channel === 'telegram' ||
    channel === 'messenger' ||
    channel === 'dashboard'
  const label = known
    ? t(`eric_activity.channel.${channel}`, channel)
    : channel
  return <span className="uppercase tracking-wide">{label}</span>
}

function DetailRow({
  label,
  value,
  className,
}: {
  label: string
  value: string
  className?: string
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`mt-0.5 whitespace-pre-wrap ${className ?? ''}`}>
        {value}
      </div>
    </div>
  )
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-9 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
        active
          ? 'border-b-2 border-primary text-foreground'
          : 'border-b-2 border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  )
}
